/**
 * เพื่อน and คู่แค้น (systems/social.ts): the friends list and the sworn-enemies list, saved in `progress.social`.
 * Data and pure helpers only. People are known by name (offline: the simulated players, the arena roster and the
 * fellows; online the server owns the lists: docs/ONLINE.md).
 */

export const SOCIAL = {
  /** Most friends, sworn enemies and waiting friend requests kept. */
  maxFriends: 50,
  maxEnemies: 30,
  maxRequests: 20,
  /** Seconds after a death by a red name in which `enemyAdd` may still name the killer. */
  offerWindow: 120,
  /** Seconds between the killer's last blow and the death for the kill to count as theirs. */
  blowWindow: 5,
  /** Metres: a sworn enemy this near is "in view" (the alert and the icon); they leave it past `nearRange + nearSlack`. */
  nearRange: 40,
  nearSlack: 10,
  /** Seconds between the checks for sworn enemies in view. */
  nearCheck: 0.5,
  /** Longest name kept. */
  maxName: 24,
} as const;

export interface FriendEntry {
  readonly name: string;
  /** About when they became friends (epoch seconds, from the save's clock). */
  readonly since: number;
}

export interface EnemyEntry {
  readonly name: string;
  /** About when they were sworn (epoch seconds). */
  readonly since: number;
  /** How many times they have killed the player. */
  kills: number;
  /** About when they last did (epoch seconds). */
  lastAt: number;
}

export interface SocialSave {
  friends: FriendEntry[];
  enemies: EnemyEntry[];
  /** Friend requests waiting for an answer (names; online the server fills it). */
  requests: string[];
}

export const newSocial = (): SocialSave => ({ friends: [], enemies: [], requests: [] });

/** How the player stands toward a name. */
export type Relation = 'friend' | 'enemy' | null;

export function relationOf(s: SocialSave | undefined, name: string): Relation {
  if (!s) return null;
  if (s.enemies.some((e) => e.name === name)) return 'enemy';
  if (s.friends.some((f) => f.name === name)) return 'friend';
  return null;
}

const okName = (n: unknown): n is string => typeof n === 'string' && n.length > 0 && n.length <= SOCIAL.maxName;
const num = (v: unknown, d = 0) => (typeof v === 'number' && Number.isFinite(v) ? v : d);

/** A save's `social`, cleaned: names valid and unique, caps kept. */
export function parseSocial(raw: unknown): SocialSave {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const seen = new Set<string>();
  const friends: FriendEntry[] = [];
  for (const f of Array.isArray(r.friends) ? r.friends : []) {
    const o = f as Record<string, unknown>;
    if (!okName(o?.name) || seen.has(o.name) || friends.length >= SOCIAL.maxFriends) continue;
    seen.add(o.name);
    friends.push({ name: o.name, since: num(o.since) });
  }
  const enemies: EnemyEntry[] = [];
  const foes = new Set<string>();
  for (const e of Array.isArray(r.enemies) ? r.enemies : []) {
    const o = e as Record<string, unknown>;
    if (!okName(o?.name) || foes.has(o.name) || enemies.length >= SOCIAL.maxEnemies) continue;
    foes.add(o.name);
    enemies.push({ name: o.name, since: num(o.since), kills: Math.max(1, Math.floor(num(o.kills, 1))), lastAt: num(o.lastAt) });
  }
  const requests = [
    ...new Set((Array.isArray(r.requests) ? r.requests : []).filter(okName).filter((n) => !seen.has(n))),
  ].slice(0, SOCIAL.maxRequests);
  return { friends, enemies, requests };
}
